import { useEffect, useState } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { API } from "../../context/AuthContext";

const norm = (s) => (s || "").replace(/\s+/g, " ").trim();

function findLoose(text, quote) {
  const chars = [];
  const map = [];
  for (let i = 0; i < text.length; i++) {
    if (!/\s/.test(text[i])) { chars.push(text[i].toLowerCase()); map.push(i); }
  }
  const hay = chars.join("");
  const needle = quote.replace(/\s+/g, "").toLowerCase();
  for (const len of [needle.length, 80, 40, 20]) {
    const n = needle.slice(0, Math.min(len, needle.length));
    if (n.length < 6) break;
    const at = hay.indexOf(n);
    if (at !== -1) return [map[at], map[at + n.length - 1] + 1];
  }
  return null;
}

function Highlighted({ text, quote }) {
  const t = norm(text);
  const hit = norm(quote).length > 5 ? findLoose(t, norm(quote)) : null;
  if (!hit) return <span>{t}</span>;
  const [a, b] = hit;
  return (
    <span>
      {t.slice(0, a)}
      <mark className="bg-amber-400/80 text-gray-950 rounded px-0.5" data-testid="source-highlight">{t.slice(a, b)}</mark>
      {t.slice(b)}
    </span>
  );
}

export default function SourceViewer({ documentId, page: startPage, quote, onClose }) {
  const [page, setPage] = useState(startPage || 1);
  const [data, setData] = useState(null);
  const [img, setImg] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let url = null;
    setData(null);
    setImg(null);
    setErr("");
    axios.get(`${API}/documents/${documentId}/pages/${page}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "Page unavailable"));
    axios.get(`${API}/documents/${documentId}/pages/${page}/image`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setImg(url); })
      .catch(() => setImg(""));
    return () => url && URL.revokeObjectURL(url);
  }, [documentId, page]);

  const total = data?.total || 1;
  return (
    <div className="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-3" onClick={onClose} data-testid="source-viewer">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#131f3a] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#2dd4bf]">Source evidence</div>
            <div className="text-sm font-bold text-white truncate" data-testid="source-viewer-name">{data?.name || "Loading…"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="source-prev-page" className="p-1.5 rounded border border-white/10 text-gray-300 disabled:opacity-30 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs font-mono text-gray-300" data-testid="source-page-indicator">Page {page} / {total}</span>
            <button disabled={page >= total} onClick={() => setPage((p) => p + 1)} data-testid="source-next-page" className="p-1.5 rounded border border-white/10 text-gray-300 disabled:opacity-30 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={onClose} data-testid="source-viewer-close" className="p-1.5 text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {quote && page === startPage && (
          <div className="px-5 py-2 text-xs text-amber-200 bg-amber-500/10 border-b border-amber-500/20 font-mono" data-testid="source-viewer-quote">Cited: “{quote}”</div>
        )}
        <div className="grid md:grid-cols-2 gap-0 flex-1 min-h-0 overflow-hidden">
          <div className="bg-black/40 flex items-start justify-center overflow-auto p-3 min-h-[240px]">
            {img === null ? <Loader2 className="w-5 h-5 text-gray-500 animate-spin mt-10" />
              : img ? <img src={img} alt={`Page ${page}`} className="max-w-full rounded shadow" data-testid="source-page-image" />
                : <div className="text-xs text-gray-500 mt-10">No page image for this file type — see extracted text.</div>}
          </div>
          <div className="overflow-auto p-4 text-xs leading-relaxed text-gray-300 whitespace-pre-wrap border-l border-white/5" data-testid="source-page-text">
            {err ? <span className="text-rose-300">{err}</span> : data ? <Highlighted text={data.text} quote={page === startPage ? quote : ""} /> : "Loading…"}
          </div>
        </div>
      </div>
    </div>
  );
}
